/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { Search } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import useT from '@/i18n/useT'
import type { ReviewWorkflow } from '@/graphql'

import { getWorkflowLabel } from './ReviewWorkflowTabs'

const getObjectSearchLabel = (
  workflow: ReviewWorkflow,
  t: ReturnType<typeof useT>['t'],
) => {
  if (workflow === 'CLASSIFICATION') {
    return t('review.search.batchId', 'Batch ID')
  }
  if (workflow === 'CORRECTION') {
    return t('review.search.boxId', 'Box ID')
  }

  return t('review.search.imageId', 'Image ID')
}

const getObjectSearchPlaceholder = (
  workflow: ReviewWorkflow,
  t: ReturnType<typeof useT>['t'],
) => {
  if (workflow === 'CLASSIFICATION') {
    return t(
      'review.search.batchIdPlaceholder',
      'Enter classification batch ID',
    )
  }
  if (workflow === 'CORRECTION') {
    return t('review.search.boxIdPlaceholder', 'Enter correction box ID')
  }

  return t('review.search.imageIdPlaceholder', 'Enter image ID')
}

const getObjectSearchTitle = (
  workflow: ReviewWorkflow,
  t: ReturnType<typeof useT>['t'],
) => {
  if (workflow === 'CLASSIFICATION') {
    return t('review.search.byBatchTitle', 'Search by batch ID')
  }
  if (workflow === 'CORRECTION') {
    return t('review.search.byBoxTitle', 'Search by box ID')
  }

  return t('review.search.byImageTitle', 'Search by image ID')
}

type ReviewSearchControlsProps = {
  mode: 'BY_USER' | 'BY_OBJECT'
  email: string
  objectId: string
  workflow: ReviewWorkflow
  loading?: boolean
  searchingMessage?: string | null
  onEmailChange: (value: string) => void
  onObjectIdChange: (value: string) => void
  onSearch: () => void
}

function ReviewSearchControls({
  mode,
  email,
  objectId,
  workflow,
  loading = false,
  searchingMessage = null,
  onEmailChange,
  onObjectIdChange,
  onSearch,
}: ReviewSearchControlsProps) {
  const { t } = useT()
  const isUserMode = mode === 'BY_USER'

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6">
            {isUserMode
              ? t('review.search.byUserTitle', 'Search by verifier email')
              : getObjectSearchTitle(workflow, t)}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
              alignItems: 'start',
            }}
          >
            {isUserMode ? (
              <TextField
                fullWidth
                label={t('review.search.userEmail', 'Verifier email')}
                value={email}
                onChange={event => onEmailChange(event.target.value)}
                placeholder={t(
                  'review.search.userEmailPlaceholder',
                  'verifier@example.com',
                )}
              />
            ) : (
              <TextField
                fullWidth
                label={getObjectSearchLabel(workflow, t)}
                value={objectId}
                onChange={event => onObjectIdChange(event.target.value)}
                placeholder={getObjectSearchPlaceholder(workflow, t)}
              />
            )}
            <Button
              variant="contained"
              startIcon={
                loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <Search />
                )
              }
              onClick={onSearch}
              disabled={
                loading || (isUserMode ? !email.trim() : !objectId.trim())
              }
              sx={{ minWidth: 152, height: 56 }}
            >
              {loading
                ? t('review.search.loading', 'Searching...')
                : t('review.search.action', 'Search')}
            </Button>
          </Box>
          {searchingMessage ? (
            <Typography variant="body2" color="text.secondary">
              {searchingMessage}
            </Typography>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  )
}

export default ReviewSearchControls
